interface DropdownEntry {
	dropdown: HTMLElement;
	originalParent: HTMLElement;
	originalNextSibling: Node | null;
}

class DropdownAnimator {
	private nav: HTMLElement;
	private backdrop: HTMLElement;
	private activeItem: HTMLElement | null = null;
	private isVisible = false;
	private closeTimer: number | null = null;
	private openRafId: number | null = null;
	private readonly CLOSE_DELAY = 200;
	private dropdownMap: Map<HTMLElement, DropdownEntry> = new Map();
	private boundBackdropEnter: () => void;
	private boundBackdropLeave: () => void;
	private scrollLockActive = false;

	constructor(nav: HTMLElement) {
		this.nav = nav;
		this.backdrop = document.createElement('div');
		this.backdrop.className = 'header-nav-menu__dropdown-backdrop';
		this.nav.appendChild(this.backdrop);
		this.collectDropdowns();

		this.boundBackdropEnter = this.cancelClose.bind(this);
		this.boundBackdropLeave = this.close.bind(this);
		this.backdrop.addEventListener('mouseenter', this.boundBackdropEnter);
		this.backdrop.addEventListener('mouseleave', this.boundBackdropLeave);
	}

	private enableScrollLock(): void {
		if (this.scrollLockActive) return;
		window.lenis?.stop();
		this.scrollLockActive = true;
	}

	private disableScrollLock(): void {
		if (!this.scrollLockActive) return;
		window.lenis?.start();
		this.scrollLockActive = false;
	}

	private collectDropdowns(): void {
		const items = this.nav.querySelectorAll<HTMLElement>(
			'.header-nav-menu__nav-item--has-dropdown'
		);

		items.forEach((item) => {
			const dropdown = item.querySelector<HTMLElement>('.header-nav-menu__dropdown');
			if (dropdown) {
				const originalParent = dropdown.parentElement ?? item;
				const originalNextSibling = dropdown.nextSibling;
				this.dropdownMap.set(item, { dropdown, originalParent, originalNextSibling });
				this.backdrop.appendChild(dropdown);
				dropdown.setAttribute('data-lenis-prevent', '');
			}
		});
	}

	private restoreDropdowns(): void {
		this.dropdownMap.forEach(({ dropdown, originalParent, originalNextSibling }) => {
			if (originalNextSibling) {
				originalParent.insertBefore(dropdown, originalNextSibling);
			} else {
				originalParent.appendChild(dropdown);
			}
		});
		this.dropdownMap.clear();
	}

	private getActiveDropdown(): HTMLElement | null {
		if (!this.activeItem) return null;
		return this.dropdownMap.get(this.activeItem)?.dropdown ?? null;
	}

	open(item: HTMLElement): void {
		if (this.closeTimer) {
			clearTimeout(this.closeTimer);
			this.closeTimer = null;
		}

		const entry = this.dropdownMap.get(item);
		if (!entry) return;

		if (this.activeItem === item) return;

		const { dropdown } = entry;

		dropdown.style.maxHeight = `${this.computeDropdownMaxHeight()}px`;

		const targetLeft = this.getDropdownLeft(item);
		const targetHeight = this.measureDropdownHeight(dropdown);

		const prevDropdown = this.getActiveDropdown();
		const prevNavLink = this.activeItem?.querySelector<HTMLAnchorElement>(
			'.header-nav-menu__nav-link'
		);
		if (prevDropdown && prevNavLink) {
			prevNavLink.classList.remove('is-open');
			prevDropdown.classList.remove('is-open');
		}

		this.activeItem = item;

		const navLink = item.querySelector<HTMLAnchorElement>('.header-nav-menu__nav-link');
		if (navLink) {
			navLink.classList.add('is-open');
		}

		if (!this.isVisible) {
			this.backdrop.style.transition = 'none';
			this.backdrop.style.transform = `translateX(${targetLeft}px)`;
			this.backdrop.style.height = `${targetHeight}px`;

			dropdown.style.transition = 'none';
			dropdown.classList.add('is-open');

			void this.backdrop.offsetHeight;

			this.backdrop.style.transition = '';
			this.backdrop.classList.add('is-visible');

			this.openRafId = requestAnimationFrame(() => {
				dropdown.style.transition = '';
				this.openRafId = null;
			});

			this.isVisible = true;
			this.enableScrollLock();
		} else {
			this.backdrop.style.transform = `translateX(${targetLeft}px)`;
			this.backdrop.style.height = `${targetHeight}px`;
			dropdown.classList.add('is-open');
		}
	}

	close(): void {
		if (this.closeTimer) {
			clearTimeout(this.closeTimer);
		}

		this.closeTimer = window.setTimeout(() => {
			this.forceClose();
			this.closeTimer = null;
		}, this.CLOSE_DELAY);
	}

	cancelClose(): void {
		if (this.closeTimer) {
			clearTimeout(this.closeTimer);
			this.closeTimer = null;
		}
	}

	forceClose(): void {
		const activeDropdown = this.getActiveDropdown();
		if (activeDropdown && this.activeItem) {
			const navLink = this.activeItem.querySelector<HTMLAnchorElement>(
				'.header-nav-menu__nav-link'
			);
			if (navLink) {
				navLink.classList.remove('is-open');
			}
			activeDropdown.classList.remove('is-open');
		}

		this.backdrop.classList.remove('is-visible');
		document.body.classList.remove('dropdown-open');
		this.activeItem = null;
		this.isVisible = false;
		this.disableScrollLock();
	}

	private getDropdownLeft(item: HTMLElement): number {
		const navRect = this.nav.getBoundingClientRect();
		const itemRect = item.getBoundingClientRect();
		const viewportPadding = 16;
		const backdropLeft = navRect.left + this.backdrop.offsetLeft;
		const minLeft = viewportPadding - backdropLeft;
		const maxLeft =
			document.documentElement.clientWidth -
			viewportPadding -
			backdropLeft -
			this.backdrop.offsetWidth;
		const itemLeft = itemRect.left - navRect.left;

		return Math.max(minLeft, Math.min(itemLeft, maxLeft));
	}

	private computeDropdownMaxHeight(): number {
		const navBottom = this.nav.getBoundingClientRect().bottom;
		const breathingRoom = 16;
		return Math.max(0, window.innerHeight - navBottom - breathingRoom);
	}

	private measureDropdownHeight(dropdown: HTMLElement): number {
		const wasHidden = !dropdown.classList.contains('is-open');
		if (wasHidden) {
			dropdown.style.visibility = 'hidden';
			dropdown.style.opacity = '0';
			dropdown.style.display = 'block';
			dropdown.style.position = 'relative';
			dropdown.style.pointerEvents = 'none';
		}

		const height = dropdown.offsetHeight;

		if (wasHidden) {
			dropdown.style.visibility = '';
			dropdown.style.opacity = '';
			dropdown.style.display = '';
			dropdown.style.position = '';
			dropdown.style.pointerEvents = '';
		}

		return height;
	}

	getActiveItem(): HTMLElement | null {
		return this.activeItem;
	}

	destroy(): void {
		if (this.closeTimer) {
			clearTimeout(this.closeTimer);
		}
		if (this.openRafId !== null) {
			cancelAnimationFrame(this.openRafId);
		}
		this.backdrop.removeEventListener('mouseenter', this.boundBackdropEnter);
		this.backdrop.removeEventListener('mouseleave', this.boundBackdropLeave);
		this.forceClose();
		this.restoreDropdowns();
		this.backdrop.remove();
	}
}

export default DropdownAnimator;
